/**
 * Client-only state for the DPS-page schedule preview (08-PRV §5.3). All of it is
 * ephemeral: it is written nowhere, never enters the scenario save payload, is cleared
 * when the schedule recomputes, and is disposed when the form unmounts.
 *
 * The ephemeral edit session is held as a `ScheduleOverrideEntry[]` — the SAME shape the
 * eval dialog's working log uses — so the shared edit layer (`useEditLayer`) transforms
 * it byte-identically. Each entry is one slot's `(milestone -> ISO day)` override map, so
 * the array is exactly the spec's `(slotId, milestone) -> ISO day` session map in list
 * form. These are module-scoped atoms (the pattern the eval dialog uses); the preview
 * resets them on mount + unmount so a session never leaks across form visits.
 */

import { atom } from 'jotai';
import {
  EMPTY_SELECTION,
  type PhaseKey,
  type ScheduleOverrideEntry,
  type SelectionSet,
} from 'pages/packages/show/eval3-new/schedule-dialog';

/** The ephemeral override session — the throwaway "Set by you" edits. */
export const ephemeralScheduleAtom = atom<ScheduleOverrideEntry[]>([]);

/** Selected milestone points (`Set<slotId|field>`) — ids + count only. */
export const previewSelectionAtom = atom<SelectionSet>(EMPTY_SELECTION);

/** Expanded numeric area-group ids (groups collapse by default). */
export const previewExpandedGroupsAtom = atom<Set<number>>(new Set<number>());

/** Whether the trailing "Unscheduled" pseudo-group is expanded. */
export const previewUnscheduledExpandedAtom = atom<boolean>(false);

/** Future-locations-only toggle (default ON). */
export const previewFutureOnlyAtom = atom<boolean>(true);

/** Free-text search (well name / API / slot id). */
export const previewSearchAtom = atom<string>('');

/** Active phase filter, or null for all phases. */
export const previewPhaseFilterAtom = atom<PhaseKey | null>(null);
